// ===========================================
// Permission Slip Editor (Admin)
// ===========================================
// Serves BOTH /permission-slips/new and /permission-slips/:id, like the
// Save-the-Date editor. On an existing slip it also shows the response
// tracking view ("12 of 18 signed").

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import BlockIcon from '@mui/icons-material/Block';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SlipEmailPreviewDialog from '../../components/permissionSlips/SlipEmailPreviewDialog';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  usePermissionSlip,
  useCreatePermissionSlip,
  useUpdatePermissionSlip,
  useSendPermissionSlip,
  useClosePermissionSlip,
  useSlipStats,
  useSlipResponses,
  useRemindPermissionSlip,
  useRefreshSlipAudience,
} from '../../hooks/usePermissionSlips';
import { useEvents } from '../../hooks/useEvents';
import { useFamilies } from '../../hooks/useUsers';
import { useLessons } from '../../hooks/useLessons';
import { useAgeGroups, useProgramLevels } from '../../hooks/useAdmin';
import { formatUTCDate } from '../../utils/dateUtils';
import { unescapeHtml } from '../../utils/sanitize';
import { openSignedSlipPdf } from '../../components/permissionSlips/downloadSlipPdf';
import { permissionSlipApi } from '../../api/permissionSlips.api';
import { useSnackbar } from 'notistack';
import type { PermissionSlipInput } from '../../api/permissionSlips.api';

const VISIBILITY_OPTIONS = [
  { value: 'ALL_FAMILIES', label: 'All families' },
  // Resolved from EventRSVP on the linked event. Requires an event, so
  // selecting it reveals the (otherwise optional) event picker and blocks save
  // until one is chosen.
  { value: 'EVENT_ATTENDEES', label: 'Everyone attending the event' },
  { value: 'SPECIFIC_CLASSES', label: 'Specific classes' },
  { value: 'SPECIFIC_AGE_GROUPS', label: 'Specific age groups' },
  { value: 'SPECIFIC_PROGRAM_LEVELS', label: 'Specific program levels' },
  { value: 'SPECIFIC_FAMILIES', label: 'Specific families' },
];

export default function PermissionSlipEditorPage() {
  const { schoolSlug, id } = useParams<{ schoolSlug: string; id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isNew = !id || id === 'new';

  const { data: slip, isLoading } = usePermissionSlip(isNew ? '' : id!);
  // useEvents returns a PAGINATED result, not an array.
  const { data: eventsResult } = useEvents();
  const events = eventsResult?.data ?? [];
  const { data: stats } = useSlipStats(isNew ? '' : id!);
  const { data: responses = [] } = useSlipResponses(isNew ? '' : id!);

  const createMutation = useCreatePermissionSlip();
  const updateMutation = useUpdatePermissionSlip();
  const sendMutation = useSendPermissionSlip();
  const closeMutation = useClosePermissionSlip();
  const remindMutation = useRemindPermissionSlip();
  const refreshMutation = useRefreshSlipAudience();

  const [form, setForm] = useState<PermissionSlipInput>({
    title: '',
    body: '',
    deadline: null,
    eventId: null,
    visibility: 'ALL_FAMILIES',
    requiresMediaConsent: false,
    requiresMedicalConsent: false,
    requiresTransportConsent: false,
    requiresDietaryInfo: false,
  });
  const [sendOpen, setSendOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);
  const [refreshOpen, setRefreshOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Audience targets. Held separately from `form` because each "specific"
  // visibility uses exactly one of these lists, and switching visibility must
  // not silently carry a stale selection from a different dimension.
  const [targetFamilies, setTargetFamilies] = useState<string[]>([]);
  const [targetClasses, setTargetClasses] = useState<string[]>([]);
  const [targetAgeGroups, setTargetAgeGroups] = useState<string[]>([]);
  const [targetProgramLevels, setTargetProgramLevels] = useState<string[]>([]);

  const { data: familiesData, isLoading: isLoadingFamilies } = useFamilies();
  const { data: lessonsData, isLoading: isLoadingLessons } = useLessons();
  const { data: ageGroupsData, isLoading: isLoadingAgeGroups } = useAgeGroups();
  const { data: programLevelsData, isLoading: isLoadingProgramLevels } = useProgramLevels();

  type TargetOption = { id: string; label: string };

  // Label fields mirror EventEditorPage exactly — the same four pickers over
  // the same four entities. Program levels prefer displayName; families use
  // `name` (there is no familyName field on this payload).
  const familyOptions: TargetOption[] = (familiesData ?? []).map((f) => ({
    id: f.id,
    label: f.name,
  }));
  const classOptions: TargetOption[] = (lessonsData ?? []).map((l) => ({
    id: l.id,
    label: l.name,
  }));
  const ageGroupOptions: TargetOption[] = (ageGroupsData ?? []).map((ag) => ({
    id: ag.id,
    label: ag.name,
  }));
  const programLevelOptions: TargetOption[] = (programLevelsData ?? []).map((pl) => ({
    id: pl.id,
    label: pl.displayName ?? pl.name,
  }));

  useEffect(() => {
    if (!slip) return;
    setForm({
      title: slip.title,
      // Stored ESCAPED — unescape for editing, or saving re-escapes and the
      // value compounds (&amp; -> &amp;amp; -> ...) on every edit.
      body: unescapeHtml(slip.body),
      // deadline is @db.Date — slice to yyyy-MM-dd for <input type="date">
      // rather than going through a Date, which would shift the day.
      deadline: slip.deadline ? slip.deadline.slice(0, 10) : null,
      eventId: slip.eventId,
      visibility: slip.visibility,
      requiresMediaConsent: slip.requiresMediaConsent,
      requiresMedicalConsent: slip.requiresMedicalConsent,
      requiresTransportConsent: slip.requiresTransportConsent,
      requiresDietaryInfo: slip.requiresDietaryInfo,
    });
    setTargetFamilies(slip.targetFamilies ?? []);
    setTargetClasses(slip.targetClasses ?? []);
    setTargetAgeGroups(slip.targetAgeGroups ?? []);
    setTargetProgramLevels(slip.targetProgramLevels ?? []);
  }, [slip]);

  // Once sent, the audience and questions are settled — editing them would
  // change what families were asked after some have already answered.
  const isSent = !!slip && (slip.status === 'SENT' || slip.status === 'CLOSED' || slip.status === 'FAILED');
  const isEventAudience = form.visibility === 'EVENT_ATTENDEES';
  const set = (patch: Partial<PermissionSlipInput>) => setForm((p) => ({ ...p, ...patch }));

  /**
   * A "specific" audience with nothing selected resolves to ZERO students
   * server-side, and sending is one-shot: the slip is claimed, stamped SENT and
   * `sentAt` is set before the audience is resolved, so it reaches nobody and
   * cannot be re-sent without a reschedule. Block it at save time.
   */
  const validate = (): boolean => {
    if (form.visibility === 'EVENT_ATTENDEES' && !form.eventId) {
      // Without an event there is nothing to resolve attendees from, so the
      // audience would be empty — and sending is one-shot, so the slip would be
      // consumed having reached nobody.
      setValidationError('Please choose the event these attendees are registered for.');
      return false;
    }
    if (form.visibility === 'SPECIFIC_FAMILIES' && targetFamilies.length === 0) {
      setValidationError('Please select at least one family.');
      return false;
    }
    if (form.visibility === 'SPECIFIC_CLASSES' && targetClasses.length === 0) {
      setValidationError('Please select at least one class.');
      return false;
    }
    if (form.visibility === 'SPECIFIC_AGE_GROUPS' && targetAgeGroups.length === 0) {
      setValidationError('Please select at least one age group.');
      return false;
    }
    if (form.visibility === 'SPECIFIC_PROGRAM_LEVELS' && targetProgramLevels.length === 0) {
      setValidationError('Please select at least one program level.');
      return false;
    }
    setValidationError(null);
    return true;
  };

  // Send only the list the chosen visibility actually uses — persisting a stale
  // selection from a previously-chosen dimension would silently widen or narrow
  // the audience if the visibility were changed back later.
  const buildPayload = (): PermissionSlipInput => ({
    ...form,
    targetFamilies: form.visibility === 'SPECIFIC_FAMILIES' ? targetFamilies : [],
    targetClasses: form.visibility === 'SPECIFIC_CLASSES' ? targetClasses : [],
    targetAgeGroups: form.visibility === 'SPECIFIC_AGE_GROUPS' ? targetAgeGroups : [],
    targetProgramLevels:
      form.visibility === 'SPECIFIC_PROGRAM_LEVELS' ? targetProgramLevels : [],
  });

  const handleSave = async () => {
    if (!validate()) return;
    const payload = buildPayload();
    if (isNew) {
      const created = await createMutation.mutateAsync(payload);
      navigate(`/${schoolSlug}/admin/permission-slips/${created.id}`);
    } else {
      await updateMutation.mutateAsync({ id: id!, data: payload });
    }
  };

  if (!isNew && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const signedPct = stats && stats.total > 0 ? (stats.SIGNED / stats.total) * 100 : 0;

  return (
    <Box>
      <PageHeader
        title={isNew ? 'New Permission Slip' : slip?.title || 'Permission Slip'}
        subtitle={isNew ? 'Ask families for permission' : undefined}
        breadcrumbs={[
          { label: 'Permission Slips', path: '/admin/permission-slips' },
          { label: isNew ? 'New' : slip?.title || '' },
        ]}
      />

      {isSent && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This permission slip has been sent. The audience and questions are locked — changing them
          now would mean families answered a different form.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card elevation={0}>
            <CardContent>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Title"
                  value={form.title}
                  onChange={(e) => set({ title: e.target.value })}
                  inputProps={{ maxLength: 200 }}
                />
                <TextField
                  fullWidth
                  required
                  multiline
                  minRows={5}
                  label="What are you asking permission for?"
                  placeholder="Describe the activity, where and when, and anything parents should know."
                  value={form.body}
                  onChange={(e) => set({ body: e.target.value })}
                  inputProps={{ maxLength: 10000 }}
                />
                <TextField
                  fullWidth
                  type="date"
                  label="Response deadline"
                  InputLabelProps={{ shrink: true }}
                  value={form.deadline ?? ''}
                  onChange={(e) => set({ deadline: e.target.value || null })}
                />

                <FormControl fullWidth disabled={isSent}>
                  <InputLabel>Who is this for?</InputLabel>
                  <Select
                    label="Who is this for?"
                    value={form.visibility}
                    onChange={(e) => set({ visibility: e.target.value })}
                  >
                    {VISIBILITY_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Target pickers — shown only for the matching "specific"
                    visibility. Without these, choosing "Specific classes" sent
                    the slip to nobody with no way to say which classes. */}
                {form.visibility === 'SPECIFIC_FAMILIES' && (
                  <Autocomplete
                    multiple
                    disabled={isSent}
                    options={familyOptions}
                    value={familyOptions.filter((o) => targetFamilies.includes(o.id))}
                    loading={isLoadingFamilies}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    onChange={(_, selected) => setTargetFamilies(selected.map((o) => o.id))}
                    renderInput={(params) => (
                      <TextField {...params} label="Select families" placeholder="Search families..." />
                    )}
                  />
                )}

                {form.visibility === 'SPECIFIC_CLASSES' && (
                  <Autocomplete
                    multiple
                    disabled={isSent}
                    options={classOptions}
                    value={classOptions.filter((o) => targetClasses.includes(o.id))}
                    loading={isLoadingLessons}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    onChange={(_, selected) => setTargetClasses(selected.map((o) => o.id))}
                    renderInput={(params) => (
                      <TextField {...params} label="Select classes" placeholder="Search classes..." />
                    )}
                  />
                )}

                {form.visibility === 'SPECIFIC_AGE_GROUPS' && (
                  <Autocomplete
                    multiple
                    disabled={isSent}
                    options={ageGroupOptions}
                    value={ageGroupOptions.filter((o) => targetAgeGroups.includes(o.id))}
                    loading={isLoadingAgeGroups}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    onChange={(_, selected) => setTargetAgeGroups(selected.map((o) => o.id))}
                    renderInput={(params) => (
                      <TextField {...params} label="Select age groups" placeholder="Search age groups..." />
                    )}
                  />
                )}

                {form.visibility === 'SPECIFIC_PROGRAM_LEVELS' && (
                  <Autocomplete
                    multiple
                    disabled={isSent}
                    options={programLevelOptions}
                    value={programLevelOptions.filter((o) => targetProgramLevels.includes(o.id))}
                    loading={isLoadingProgramLevels}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    onChange={(_, selected) => setTargetProgramLevels(selected.map((o) => o.id))}
                    renderInput={(params) => (
                      <TextField {...params} label="Select program levels" placeholder="Search program levels..." />
                    )}
                  />
                )}

                {validationError && <Alert severity="error">{validationError}</Alert>}

                {/* The event picker doubles as the audience source for
                    EVENT_ATTENDEES, so it stops being optional in that mode —
                    the label, the "standalone" choice and the helper text all
                    change rather than rendering a second near-identical
                    dropdown above. */}
                <FormControl fullWidth required={isEventAudience}>
                  <InputLabel>
                    {isEventAudience ? 'Event' : 'Linked event (optional)'}
                  </InputLabel>
                  <Select
                    label={isEventAudience ? 'Event' : 'Linked event (optional)'}
                    value={form.eventId ?? ''}
                    onChange={(e) => set({ eventId: e.target.value || null })}
                    disabled={isSent}
                  >
                    {!isEventAudience && (
                      <MenuItem value="">
                        <em>Standalone — not tied to an event</em>
                      </MenuItem>
                    )}
                    {events.map((ev) => (
                      <MenuItem key={ev.id} value={ev.id}>
                        {ev.title}
                      </MenuItem>
                    ))}
                  </Select>
                  {isEventAudience && (
                    <FormHelperText>
                      Everyone with an RSVP of Attending or Maybe will be asked. Registrations
                      that arrive later are not added automatically — use “Refresh audience”
                      after sending to pick them up.
                    </FormHelperText>
                  )}
                </FormControl>

                <Divider />

                {/* These checkboxes do NOT define what is being consented to —
                    the body field above does. They add optional questions to
                    the parent's response form. The previous heading ("What are
                    you asking parents to consent to?") described the body's
                    job, and did not fit the last item at all, which collects
                    free text rather than permission. */}
                <Box>
                  <Typography variant="subtitle2">Ask parents about</Typography>
                  <Typography variant="caption" color="text.secondary">
                    The slip text above is what parents agree to. These add specific questions to
                    their response form.
                  </Typography>
                </Box>
                <Stack>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.requiresMediaConsent}
                        disabled={isSent}
                        onChange={(e) => set({ requiresMediaConsent: e.target.checked })}
                      />
                    }
                    label="Photos / media"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.requiresMedicalConsent}
                        disabled={isSent}
                        onChange={(e) => set({ requiresMedicalConsent: e.target.checked })}
                      />
                    }
                    label="Emergency medical treatment"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.requiresTransportConsent}
                        disabled={isSent}
                        onChange={(e) => set({ requiresTransportConsent: e.target.checked })}
                      />
                    }
                    label="Transport"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.requiresDietaryInfo}
                        disabled={isSent}
                        onChange={(e) => set({ requiresDietaryInfo: e.target.checked })}
                      />
                    }
                    label="Dietary / medical notes (free text)"
                  />
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={
                      !form.title.trim() ||
                      !form.body.trim() ||
                      createMutation.isPending ||
                      updateMutation.isPending
                    }
                  >
                    {isNew ? 'Create' : 'Save changes'}
                  </Button>
                  {!isNew && (
                    // Previews the UNSAVED form values, so an edit can be checked
                    // before it is saved. Available in every status: harmless.
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      // An empty title or body has nothing to preview (the server
                      // refuses one too).
                      disabled={!form.title.trim() || !form.body.trim()}
                      onClick={() => setPreviewOpen(true)}
                    >
                      Preview email
                    </Button>
                  )}
                  {!isNew && !isSent && (
                    <Button
                      variant="outlined"
                      startIcon={<SendIcon />}
                      onClick={() => setSendOpen(true)}
                    >
                      Send now
                    </Button>
                  )}
                  {!isNew && slip?.status === 'SENT' && (
                    <>
                      {/*
                        The action an admin actually performs two days before a
                        camp. Without it the only lever was "reschedule and send
                        to everyone", which re-emails families who already
                        signed.
                      */}
                      <Button
                        variant="outlined"
                        startIcon={<NotificationsActiveIcon />}
                        disabled={remindMutation.isPending || (stats?.PENDING ?? 0) === 0}
                        onClick={() => setRemindOpen(true)}
                      >
                        Remind {stats?.PENDING ? `(${stats.PENDING})` : ''}
                      </Button>
                      <Button
                        startIcon={<GroupAddIcon />}
                        disabled={refreshMutation.isPending}
                        onClick={() => setRefreshOpen(true)}
                      >
                        Refresh audience
                      </Button>
                      <Button
                        color="inherit"
                        startIcon={<BlockIcon />}
                        onClick={() => setCloseOpen(true)}
                      >
                        Close
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* --- Response tracking --- */}
        <Grid item xs={12} md={5}>
          {!isNew && stats && stats.total > 0 && (
            <Card elevation={0}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {stats.SIGNED} of {stats.total} signed
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={signedPct}
                  sx={{ height: 8, borderRadius: 4, mb: 2 }}
                />
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip size="small" label={`${stats.PENDING} pending`} color="warning" />
                  <Chip size="small" label={`${stats.SIGNED} signed`} color="success" />
                  {stats.DECLINED > 0 && (
                    <Chip size="small" label={`${stats.DECLINED} declined`} color="error" />
                  )}
                </Stack>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Student</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Slip</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {responses.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>
                            {r.Student.firstName} {r.Student.lastName}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={r.status.toLowerCase()}
                              color={
                                r.status === 'SIGNED'
                                  ? 'success'
                                  : r.status === 'DECLINED'
                                    ? 'error'
                                    : 'warning'
                              }
                            />
                            {r.signedAt && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {formatUTCDate(r.signedAt)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {/*
                              Streamed by an authenticated route — these PDFs
                              contain medical detail and are never public files.
                            */}
                            {r.signedPdfUrl && (
                              <Button
                                size="small"
                                startIcon={<PictureAsPdfIcon />}
                                onClick={() =>
                                  openSignedSlipPdf(
                                    () => permissionSlipApi.fetchPdf(r.id),
                                    (m) => enqueueSnackbar(m, { variant: 'error' })
                                  )
                                }
                              >
                                PDF
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {!isNew && (
        <SlipEmailPreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          slipId={id!}
          draft={{ title: form.title, body: form.body, deadline: form.deadline ?? null }}
        />
      )}

      <ConfirmDialog
        open={sendOpen}
        title="Send this permission slip?"
        message="Every family in the audience will be emailed a link to sign. This cannot be unsent."
        confirmLabel="Send"
        onConfirm={async () => {
          await sendMutation.mutateAsync(id!).catch(() => undefined);
          setSendOpen(false);
        }}
        onCancel={() => setSendOpen(false)}
        loading={sendMutation.isPending}
      />

      <ConfirmDialog
        open={remindOpen}
        title="Send a reminder?"
        message={`Only the ${stats?.PENDING ?? 0} ${(stats?.PENDING ?? 0) === 1 ? 'family who has' : 'families who have'} not yet responded will be emailed. Families who have already signed or declined are not contacted, and their existing links keep working.`}
        confirmLabel="Send reminder"
        onConfirm={async () => {
          await remindMutation.mutateAsync(id!).catch(() => undefined);
          setRemindOpen(false);
        }}
        onCancel={() => setRemindOpen(false)}
        loading={remindMutation.isPending}
      />

      <ConfirmDialog
        open={refreshOpen}
        title="Refresh the audience?"
        message="Any student who now matches this permission slip but was not asked before — a child who enrolled after it was sent — will be added and their family emailed. Nobody who has already been asked is contacted again."
        confirmLabel="Refresh"
        onConfirm={async () => {
          await refreshMutation.mutateAsync(id!).catch(() => undefined);
          setRefreshOpen(false);
        }}
        onCancel={() => setRefreshOpen(false)}
        loading={refreshMutation.isPending}
      />

      <ConfirmDialog
        open={closeOpen}
        title="Close this permission slip?"
        message="Families will no longer be able to respond, and outstanding links are revoked. Signed responses are kept."
        confirmLabel="Close slip"
        onConfirm={async () => {
          await closeMutation.mutateAsync(id!).catch(() => undefined);
          setCloseOpen(false);
        }}
        onCancel={() => setCloseOpen(false)}
        loading={closeMutation.isPending}
      />
    </Box>
  );
}
